/*
====================================================================
formCategories.js

One place that answers two questions asked on several screens:

    which category does this form belong to?
    what do we call the person filling it in?

The portal groups forms by category, and the form pages themselves are
a separate route — SingleFormPage only receives a form id. Without a
shared module both would have to carry their own copy of the rules,
and they would drift.


TERMINOLOGY
-----------
Aesthetic  -> "patient"   (clinical treatment)
Wellness   -> "client"    (yoga, Sujok, spiritual healing)

Both are the same `clients` row underneath; only the wording differs.
The distinction is real — someone booking a yoga class isn't a patient,
and calling them one makes the clinic sound like it doesn't know which
service they came for.

Registration and anything uncategorised default to "patient", since the
registration form is the clinical one.


THE ID LABEL, AND A MISMATCH TO FIX
-----------------------------------
termsFor() also renames the identifier: "Patient ID" or "Client ID".

The credentials email in services/clientAccount.js says "Client ID"
unconditionally. If a patient registers through an Aesthetic form and
the screen calls it a Patient ID while the email calls it a Client ID,
that is a real inconsistency for the one number they must not lose.

One name is used everywhere — see ID_LABEL below — so the screens, the
PDF and the registration email all agree.
====================================================================
*/

/*
  THE ID IS CALLED "PATIENT ID" ON EVERY FORM.

  The person-noun still follows the branch — an Aesthetic form says
  patient, a Yoga or Sujok or Spiritual one says client, because
  someone booking a tarot reading is not a patient. But the NUMBER is a
  single system identifier, issued once at registration, printed on one
  email, and read out by reception from one screen. Giving it two names
  meant a client could reasonably think the Client ID on a yoga form
  and the Patient ID in their email were different things.

  So: contextual wording for the person, one fixed name for the number.
  This also puts it back in step with the registration email, which
  hands the number out before anyone knows which branch they will use.
*/
export const ID_LABEL = "Client ID";


const norm = (s) => String(s || "").toLowerCase();

const textOf = (form) => `${norm(form?.form_name)} ${norm(form?.form_type)}`;


/* The menu. Order here is the order on screen. */
export const CATEGORIES = [
    {
        id: "aesthetic",
        label: "Aesthetics",
        icon: "✦",
        blurb: "Skin, hair and cosmetic treatments",
        children: [
            { id: "skin",      label: "Skin",        blurb: "Acne, pigmentation, texture and tone" },
            { id: "hair",      label: "Hair",        blurb: "Hair fall, scalp health and thinning" },
            { id: "skin_hair", label: "Skin & Hair", blurb: "Full consultation covering both" }
        ]
    },
    {
        id: "wellness",
        label: "Energy & Wellness",
        icon: "☯",
        blurb: "Yoga, Sujok and spiritual healing",
        children: [
            { id: "yoga",      label: "Yoga",              blurb: "Class registration and consent" },
            { id: "sujok",     label: "Sujok",             blurb: "Sujok and integrative therapies" },
            { id: "spiritual", label: "Spiritual Healing", blurb: "Energy and spiritual healing" }
        ]
    }
];


/*
  Ordered. First test that passes decides the category, and the order
  matters twice:

    - "Yoga Registration & Consent Form" contains "registration", so
      the yoga rule is tested first, or every themed form would end up
      behind the Registration button.

    - "Additional Clinic Forms" is matched to HAIR: its content is the
      Hamilton-Norwood chart, a male pattern baldness scale, plus the
      session tracker and photography consent that go with it.
*/
const RULES = [
    { id: "yoga",      test: (t) => t.includes("yoga") },
    { id: "sujok",     test: (t) => t.includes("sujok") || t.includes("acupress") || t.includes("acupunct") },
    { id: "spiritual", test: (t) => t.includes("spiritual") || t.includes("reiki")
                                 || (t.includes("healing") && !t.includes("sujok")) },

    { id: "hair",      test: (t) => t.includes("additional clinic") },

    { id: "skin_hair", test: (t) => (t.includes("skin") && t.includes("hair")) || t.includes("aesthetic") },
    { id: "skin",      test: (t) => t.includes("skin") || t.includes("derma") || t.includes("facial") },
    { id: "hair",      test: (t) => t.includes("hair") || t.includes("scalp")
                                 || t.includes("baldness") || t.includes("norwood") },

    { id: "registration", test: (t) => t.includes("registration") }
];


/*
  The leaf category id: "skin", "yoga", "registration", "other"...

  May return an ARRAY when the server sends one — a form can belong to
  more than one category. The shared clinic documents are the case that
  matters: a Treatment Plan is equally a Skin and a Hair document, and
  duplicating the `forms` row to file it under both would be worse.

  Callers must handle both shapes; FormPortal wraps the result with
  Array.isArray().
*/
export function categoryOf(form) {
    if (Array.isArray(form?.category)) {
        return form.category.map(norm);
    }

    if (form?.category) return norm(form.category);

    const rule = RULES.find((r) => r.test(textOf(form)));
    return rule ? rule.id : "other";
}


/* "aesthetic" | "wellness" | null (registration and other) */
export function parentOfCategory(categoryId) {
    const parent = CATEGORIES.find((c) =>
        c.children.some((ch) => ch.id === categoryId)
    );
    return parent ? parent.id : null;
}


const PATIENT_TERMS = {
    person: "client",
    Person: "Client",
    people: "clients",
    idLabel: ID_LABEL,
    detailsTitle: "Client Details",
    formNoun: "client form"
};

const CLIENT_TERMS = {
    person: "client",
    Person: "Client",
    people: "clients",
    idLabel: ID_LABEL,          // same number, same name
    detailsTitle: "Your Details",
    formNoun: "form"
};


/*
  Pass a form (or a category id) and get the wording for that screen.
  Everything uncategorised falls back to patient wording, because the
  registration form is the clinical one.
*/
export function termsFor(formOrCategory) {
    const resolved = typeof formOrCategory === "string"
        ? formOrCategory
        : categoryOf(formOrCategory);

    /*
      A multi-category form gets wellness wording only if EVERY category
      it belongs to is under wellness. A document shared between Skin and
      Yoga is a clinical one as far as the patient reading it is
      concerned, so "patient" is the safer default.
    */
    const ids = Array.isArray(resolved) ? resolved : [resolved];

    const allWellness =
        ids.length > 0 && ids.every((id) => parentOfCategory(id) === "wellness");

    return allWellness ? CLIENT_TERMS : PATIENT_TERMS;
}

export default { CATEGORIES, categoryOf, parentOfCategory, termsFor, ID_LABEL };